/**
 * T-220: a "please upload this" email links to /portal/task#t=<token>. An inline script in
 * index.html moves the token out of the address bar before any app code or analytics loads
 * (window.__htPortalTaskToken); instrument.js does the same for a link opened in this tab without
 * a reload. This keeps it for this tab only (sessionStorage, plus memory if storage is blocked),
 * so a refresh of the page still works. A fragment never reaches a server or a log.
 */
export const TASK_TOKEN_KEY = 'ht:portal-task';

const PAGE = /^\/portal\/task\/?$/;
const TOKEN = /^[A-Za-z0-9_-]{43}$/;

type StashWindow = Window & { __htPortalTaskToken?: string };
type Loc = Pick<Location, 'hash' | 'pathname' | 'search'>;
type Hist = Pick<History, 'replaceState' | 'state'>;

let inMemory: string | null = null;

function keep(token: string): void {
  inMemory = token;
  try {
    sessionStorage.setItem(TASK_TOKEN_KEY, token);
  } catch {
    // storage blocked: the in-memory copy still serves this page view
  }
}

/** A link opened in this same tab (no reload, so index.html didn't run): take its token. */
export function captureTaskFragment(loc: Loc = window.location, hist: Hist = window.history): void {
  if (!PAGE.test(loc.pathname) || !loc.hash) return;
  try {
    // A new link replaces any earlier one, even when the new one is malformed.
    forgetTaskToken();
    const token = new URLSearchParams(loc.hash.slice(1)).get('t');
    if (token && TOKEN.test(token)) keep(token);
  } finally {
    hist.replaceState(hist.state ?? null, '', loc.pathname + loc.search);
  }
}

/** The token for this tab, if one was captured (index.html's stash is taken once). */
export function readTaskToken(): string | null {
  const w = window as StashWindow;
  const early = w.__htPortalTaskToken;
  if (early !== undefined) {
    delete w.__htPortalTaskToken;
    // A newly opened link wins over one kept from earlier in this tab.
    forgetTaskToken();
    if (TOKEN.test(early)) keep(early);
  }
  try {
    return sessionStorage.getItem(TASK_TOKEN_KEY) ?? inMemory;
  } catch {
    return inMemory;
  }
}

/** Done with the request: forget its token in this tab. */
export function forgetTaskToken(): void {
  inMemory = null;
  delete (window as StashWindow).__htPortalTaskToken;
  try {
    sessionStorage.removeItem(TASK_TOKEN_KEY);
  } catch {
    // storage blocked: nothing was kept there
  }
}
